import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";

import { isSuperAdmin } from "../../../../utils/Permissions";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import messages from "../../../../assets/locale/messages";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import Filters from "../../../../components/Filter";
import { TabSetsLevels, UIPaginationBar } from "../../../../components/ui";
import {
  getRegistrationRequestsRequest,
  editCompanyRegistrationRequest,
} from "../../../../store/RegistrationReq/actions";
import {
  ITEMS_PER_PAGE,
  rejectCompany,
} from "../../../../network/apis/companies";
import { showErrorMsg, showSuccessMsg } from "../../../../utils/Helpers";
import RegistrationTable from "./components/RegistrationTable";
import RegistrationEmptyState from "./components/RegistrationEmptyState/RegistrationEmptyState";

import "./RegistrationReqPage.scss";

const RegistrationReqPage = () => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const list = useSelector((state) => state.companiesRegistration.list);
  const meta = useSelector((state) => state.companiesRegistration.meta);
  const { shared, registrationReq } = messages[lang];

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("pending");
  const prevSearchInput = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (prevSearchInput.current !== null && prevSearchInput.current !== searchInput) {
        setPage(1);
      }
      prevSearchInput.current = searchInput;
      setDebouncedSearch(searchInput);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    dispatch(
      getRegistrationRequestsRequest({
        page,
        items: pageSize,
        status: activeStatus,
        name: debouncedSearch?.trim() || undefined,
      })
    );
  }, [dispatch, page, pageSize, debouncedSearch, activeStatus, lang]);

  const searchName = debouncedSearch?.trim() || undefined;

  const handleApprove = (id) => {
    dispatch(
      editCompanyRegistrationRequest({
        id,
        page,
        items: pageSize,
        status: activeStatus,
        name: searchName,
        data: { status: "approved" },
      })
    );
  };

  const handleReject = async (id, reason) => {
    try {
      await rejectCompany(id, reason);
      showSuccessMsg("rejectRequest");
      dispatch(
        getRegistrationRequestsRequest({
          page,
          items: pageSize,
          status: activeStatus,
          name: searchName,
        })
      );
    } catch (error) {
      const msg = error.response?.data?.message;
      if (msg) {
        showErrorMsg(msg);
      }
      throw error;
    }
  };

  if (!isSuperAdmin()) {
    return <Redirect to={ROUTE_PATHS.events} />;
  }

  const total = Number(meta.count) || 0;
  const currentPage = Number(meta.currentPage) || page;
  const from = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, total);
  const rangeTemplate =
    registrationReq.showingRange || "Showing {from}–{to} of {total} companies";

  const emptyTitle =
    activeStatus === "rejected"
      ? registrationReq.emptyRejectedTitle
      : registrationReq.emptyPendingTitle;

  const renderBody = () => {
    if (!isLoading && (!list || list.length === 0)) {
      return (
        <div className="registration-req-page__empty">
          <RegistrationEmptyState title={emptyTitle} />
        </div>
      );
    }

    if (!list || list.length === 0) {
      return null;
    }

    return (
      <>
        <RegistrationTable
          rows={list}
          status={activeStatus}
          messagesBundle={registrationReq}
          shared={shared}
          onApprove={handleApprove}
          onReject={handleReject}
        />
        <UIPaginationBar
          currentPage={currentPage}
          pages={meta.pages || 1}
          pageSize={pageSize}
          rangeTemplate={rangeTemplate}
          rangeFrom={from}
          rangeTo={to}
          rangeTotal={total}
          rowsPerPageLabel={shared.numberOfRows}
          onPageChange={(nextPage) => setPage(nextPage)}
          onPageSizeChange={(nextSize) => {
            setPageSize(nextSize);
            setPage(1);
          }}
        />
      </>
    );
  };

  return (
    <div className="registration-req-page">
      <div className="mt-3">
        <Breadcrumbs
          list={[
            {
              path: ROUTE_PATHS.companiesList,
              label: registrationReq.companiesBreadcrumb || shared.companies,
            },
            { path: "", label: registrationReq.listBreadcrumb },
          ]}
          BreadCrumbHasBackIcon={false}
        />
      </div>
      <Filters
        searchInput={{
          placeholder: registrationReq.searchPlaceholder,
          name: "registrationSearch",
          value: searchInput,
          onChange: (value) => setSearchInput(value),
        }}
        filters={[]}
      />
      <TabSetsLevels
        className="registration-req-page__status-tabs"
        value={activeStatus}
        onChange={(nextStatus) => {
          setActiveStatus(nextStatus);
          setPage(1);
        }}
        tabs={[
          {
            label:
              registrationReq.pendingTab ||
              registrationReq.statusPendingApproval ||
              "Pending",
            value: "pending",
          },
          {
            label:
              registrationReq.rejectedTab ||
              registrationReq.statusRejected ||
              "Rejected",
            value: "rejected",
          },
        ]}
      />
      <div className="d-flex flex-column justify-content-between">
        {renderBody()}
      </div>
    </div>
  );
};

export default RegistrationReqPage;
